import { isUvaldeBriefing } from '../../lib/uvalde-briefing';
import { pendingFemaLabel } from '../../lib/fema-pending';
import type { BriefingMapLayers, NearbyPipeline, NearestFeature, ParcelDetail } from '../../lib/api';
import { roadSurfaceFromCode, roadSurfaceShort, trafficWord, truckShareWords } from '../../lib/api';

export interface BriefingLayerData {
  parcel: ParcelDetail;
  carveOutSlug?: string;
  pipelines: NearbyPipeline[];
  mapLayers: BriefingMapLayers;
  nearestFeatures?: Record<string, NearestFeature | null | undefined>;
}

export interface FeatureTarget {
  layer: string;
  property: string;
  values: string[];
  point?: [number, number] | null;
  name?: string;
}

export interface LayerDefinition {
  id: string;
  label: string;
  group: string;
  dataKey: string;
  layerIds: string[];
  symbol: 'line' | 'fill' | 'circle' | 'hex';
  color: string;
}

export interface LayerFact { text: string; target?: FeatureTarget | null }
export interface LayerFacts { items: LayerFact[]; source: string }

export interface FeatureCard {
  memberId: string;
  title: string;
  distance: string;
  details: string[];
  source: string;
  target: FeatureTarget;
  feature: GeoJSON.Feature;
  layerId: string;
}

export interface MapEmphasis {
  memberIds: string[];
  target?: FeatureTarget | null;
  fiberSelected: boolean;
}

export const LAYER_DEFINITIONS: LayerDefinition[] = [
  { id: 'briefing-pipelines-desert-sw', label: 'Desert Southwest', group: 'gas', dataKey: 'pipelinesDesertSw', layerIds: ['briefing-pipelines-desert-sw', 'briefing-pipelines-desert-sw-glow'], symbol: 'line', color: '#C4705A' },
  { id: 'briefing-pipelines-gas', label: 'Transmission', group: 'gas', dataKey: 'pipelinesGas', layerIds: ['briefing-pipelines-gas-glow', 'briefing-pipelines-gas-casing', 'briefing-pipelines-gas', 'briefing-pipelines-gas-label'], symbol: 'line', color: '#ffa063' },
  { id: 'briefing-pipelines-gathering', label: 'Gathering', group: 'gas', dataKey: 'pipelinesGathering', layerIds: ['briefing-pipelines-gathering'], symbol: 'line', color: '#8A9CAA' },
  { id: 'briefing-gas-plants', label: 'Gas Plants', group: 'gas', dataKey: 'gasPlants', layerIds: ['briefing-gas-plants'], symbol: 'circle', color: '#C4705A' },
  { id: 'briefing-compressors', label: 'Compressors', group: 'gas', dataKey: 'compressors', layerIds: ['briefing-compressors'], symbol: 'circle', color: '#D4915A' },
  { id: 'briefing-wetlands-fill', label: 'Wetlands', group: 'land', dataKey: 'wetlands', layerIds: ['briefing-wetlands-fill', 'briefing-wetlands-outline'], symbol: 'fill', color: '#6A7E8E' },
  { id: 'briefing-flood-zones-fill', label: 'Floodplain', group: 'land', dataKey: 'floodZones', layerIds: ['briefing-flood-zones-fill', 'briefing-flood-zones-outline', 'briefing-flood-zones-twdb-outline', 'briefing-flood-zones-pending-outline'], symbol: 'fill', color: '#C4705A' },
  { id: 'briefing-oil-gas-wells', label: 'Oil & Gas Wells', group: 'land', dataKey: 'oilGasWells', layerIds: ['briefing-oil-gas-wells', 'briefing-oil-gas-laterals'], symbol: 'circle', color: '#D4915A' },
  { id: 'briefing-substations', label: 'Substations', group: 'power', dataKey: 'substations', layerIds: ['briefing-substations'], symbol: 'circle', color: '#C4705A' },
  { id: 'briefing-osm-switchyards', label: 'Switchyards', group: 'power', dataKey: 'osmSwitchyards', layerIds: ['briefing-osm-switchyards'], symbol: 'circle', color: '#C4705A' },
  { id: 'briefing-carrier-fiber', label: 'Carrier routes', group: 'fiber', dataKey: 'carrierRoutes', layerIds: ['briefing-carrier-fiber'], symbol: 'line', color: '#4A90D9' },
  { id: 'briefing-data-centers', label: 'Data Centers', group: 'fiber', dataKey: 'dataCenters', layerIds: ['briefing-data-centers'], symbol: 'circle', color: '#3E4E5C' },
  { id: 'briefing-water-wells', label: 'Water Wells', group: 'water', dataKey: 'waterWells', layerIds: ['briefing-water-wells'], symbol: 'circle', color: '#6A7E8E' },
  { id: 'briefing-aquifer-fill', label: 'Aquifer', group: 'water', dataKey: 'aquifers', layerIds: ['briefing-aquifer-fill', 'briefing-aquifer-outline'], symbol: 'fill', color: '#6A7E8E' },
  { id: 'briefing-highways', label: 'Highways', group: 'access', dataKey: 'highways', layerIds: ['briefing-highways'], symbol: 'line', color: '#D8DCE0' },
  { id: 'briefing-public-roads', label: 'Public Roads', group: 'access', dataKey: 'publicRoads', layerIds: ['briefing-public-roads'], symbol: 'line', color: '#C8CCD0' },
  { id: 'briefing-railroads', label: 'Railroads', group: 'access', dataKey: 'railroads', layerIds: ['briefing-railroads'], symbol: 'line', color: '#C8CCD0' },
  { id: 'briefing-airports', label: 'Airports', group: 'access', dataKey: 'airports', layerIds: ['briefing-airports'], symbol: 'circle', color: '#6A7E8E' },
  { id: 'briefing-access-route', label: 'Haul Route', group: 'access', dataKey: 'accessRoute', layerIds: ['briefing-access-route-casing', 'briefing-access-route', 'briefing-access-points'], symbol: 'line', color: '#F5E8D6' },
  { id: 'briefing-frontage-roads', label: 'Frontage & Ranch Roads', group: 'access', dataKey: 'frontageRoads', layerIds: ['briefing-frontage-roads', 'briefing-frontage-tracks'], symbol: 'line', color: '#D4915A' },
];

export const LAYER_CATEGORIES: { id: string; label: string; memberIds: string[] }[] = [
  { id: 'gas', label: 'Gas', memberIds: LAYER_DEFINITIONS.filter(d => d.group === 'gas').map(d => d.id) },
  { id: 'land', label: 'Land', memberIds: LAYER_DEFINITIONS.filter(d => d.group === 'land').map(d => d.id) },
  { id: 'power', label: 'Power', memberIds: LAYER_DEFINITIONS.filter(d => d.group === 'power').map(d => d.id) },
  { id: 'fiber', label: 'Fiber', memberIds: LAYER_DEFINITIONS.filter(d => d.group === 'fiber').map(d => d.id) },
  { id: 'water', label: 'Water', memberIds: LAYER_DEFINITIONS.filter(d => d.group === 'water').map(d => d.id) },
  { id: 'access', label: 'Access', memberIds: LAYER_DEFINITIONS.filter(d => d.group === 'access').map(d => d.id) },
];

type Position = [number, number];
type Properties = Record<string, unknown>;

const EARTH_RADIUS_MI = 3958.7613;
const UNKNOWN_DISTANCE = 'Distance unavailable';
type DistanceResult = { miles: number | null; label: string; from: Position | null; to: Position | null };
const distanceCache = new WeakMap<object, WeakMap<object, DistanceResult>>();
type Bounds = { minX: number; minY: number; maxX: number; maxY: number };
type FootprintIndex = {
  geometries: GeoJSON.Geometry[];
  polygons: Position[][][];
  points: Position[];
  segments: Array<[Position, Position]>;
  longitudeScale: number;
  bounds: Bounds | null;
};
const footprintIndexCache = new WeakMap<object, FootprintIndex>();

function finiteNumber(value: unknown): number | null {
  if (value == null || value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function positiveNumber(value: unknown): number | null {
  const parsed = finiteNumber(value);
  return parsed != null && parsed > 0 ? parsed : null;
}

function text(value: unknown): string | null {
  return value == null || value === '' ? null : String(value);
}

function validPosition(value: unknown): value is Position {
  return Array.isArray(value)
    && value.length >= 2
    && finiteNumber(value[0]) != null
    && finiteNumber(value[1]) != null;
}

function position(value: unknown): Position | null {
  return validPosition(value) ? [Number(value[0]), Number(value[1])] : null;
}

function featureProperties(value: GeoJSON.Feature): Properties {
  return (value.properties || {}) as Properties;
}

function featureCollection(data: BriefingLayerData, key: string): GeoJSON.FeatureCollection | null {
  const value = (data.mapLayers as unknown as Record<string, unknown>)[key];
  return value && typeof value === 'object' && (value as GeoJSON.FeatureCollection).type === 'FeatureCollection'
    ? value as GeoJSON.FeatureCollection
    : null;
}

function features(data: BriefingLayerData, key: string): GeoJSON.Feature[] {
  return featureCollection(data, key)?.features || [];
}

function definitionForLayer(layerId: string): LayerDefinition | undefined {
  return LAYER_DEFINITIONS.find(definition => definition.layerIds.includes(layerId));
}

function titleCase(value: unknown): string | null {
  const valueText = text(value);
  if (!valueText) return null;
  return valueText === valueText.toUpperCase()
    ? valueText.toLowerCase().replace(/\b\w/g, character => character.toUpperCase())
    : valueText;
}

function formatNumber(value: number): string {
  return value.toLocaleString(undefined, { maximumFractionDigits: 1 });
}

function roadName(value: unknown): string | null {
  return text(value)?.replace(/US Hwy /i, 'US ').replace(/(\d)\s*[NSEW]$/i, '$1') || null;
}

// 'FM 469', 'I-35', 'SH 97', 'CR 123' keep their route casing; an all-caps street name
// ('ROCKY CREEK RD') reads better title-cased.
function roadLabel(value: unknown): string | null {
  const valueText = text(value);
  if (!valueText) return null;
  return /^[A-Z]{1,2}[- ]?\d/.test(valueText) ? valueText : titleCase(valueText);
}

function unique(values: Array<string | null>): string[] {
  return [...new Set(values.filter((value): value is string => Boolean(value)))];
}

function parseList(value: unknown): Properties[] {
  try {
    const parsed = typeof value === 'string' ? JSON.parse(value) as unknown : value;
    return Array.isArray(parsed)
      ? parsed.filter(item => item != null && typeof item === 'object') as Properties[]
      : [];
  } catch {
    return [];
  }
}

function targetForFeature(layer: string, value: GeoJSON.Feature): FeatureTarget | null {
  const properties = featureProperties(value);
  const id = text(properties.id);
  const name = text(properties.name);
  const point = value.geometry?.type === 'Point' ? position(value.geometry.coordinates) : null;
  if (!id && !name && !point) return null;
  return {
    layer,
    property: id ? 'id' : 'name',
    values: id ? [id] : name ? [name] : [],
    point,
    name: name || undefined,
  };
}

function groupedTarget(layer: string, values: GeoJSON.Feature[]): FeatureTarget | null {
  const ids = values.map(value => text(featureProperties(value).id)).filter((value): value is string => Boolean(value));
  if (ids.length) return { layer, property: 'id', values: [...new Set(ids)] };
  const names = values.map(value => text(featureProperties(value).name)).filter((value): value is string => Boolean(value));
  return names.length ? { layer, property: 'name', values: [...new Set(names)] } : null;
}

// Curated large-line floor (structured_intel.large_pipeline_min_diameter_in, Meiners Ranch):
// when set, the Transmission and Gathering facts list every tap-candidate / gathering
// system at or above that diameter instead of the first transmission line and the first
// three mapped gathering features. Absent the key, the legacy shape is untouched.
function largeLineFloor(data: BriefingLayerData): number | null {
  const intel = (data.parcel as unknown as { structured_intel?: { large_pipeline_min_diameter_in?: unknown } }).structured_intel;
  return positiveNumber(intel?.large_pipeline_min_diameter_in);
}

function largeLineFacts(data: BriefingLayerData, dataKey: string, systems: NearbyPipeline[], source: string): LayerFacts {
  const rows = features(data, dataKey);
  const items = systems.map(system => {
    const label = titleCase(system.system_display_name || system.system_name || system.name || system.operator) || 'Mapped line';
    const operator = titleCase(system.operator_parent || system.operator);
    const diameter = positiveNumber(system.diameter_inches);
    const keys = new Set([`${system.name || ''}|${system.operator || ''}`]);
    for (const segment of system.segments || []) keys.add(`${segment.name || ''}|${segment.operator || ''}`);
    const matches = rows.filter(row => {
      const properties = featureProperties(row);
      return keys.has(`${text(properties.name) || ''}|${text(properties.operator) || ''}`);
    });
    const grouped = groupedTarget(dataKey, matches)
      || (system.name ? { layer: dataKey, property: 'name', values: [system.name] } : null);
    return {
      text: [label, operator, diameter ? `${formatNumber(diameter)}″` : null].filter(Boolean).join(' · '),
      target: grouped ? { ...grouped, name: label } : null,
    };
  });
  return items.length ? { items, source } : noSummary(source);
}

function targetForNearest(layer: string, value: NearestFeature | null | undefined): FeatureTarget | null {
  if (!value) return null;
  const property = value.faa_id ? 'faa_id' : 'name';
  const match = value.faa_id || value.name;
  const point = position([value.lng, value.lat]);
  return match || point ? {
    layer,
    property,
    values: match ? [String(match)] : [],
    point,
    name: value.name || undefined,
  } : null;
}

function formatDistance(miles: number): string {
  if (miles < 0.005) return '<0.01';
  return String(Number(miles.toFixed(miles < 1 ? 2 : 1)));
}

function radians(value: number): number {
  return value * Math.PI / 180;
}

function haversineMiles(a: Position, b: Position): number {
  const dLat = radians(b[1] - a[1]);
  const dLng = radians(b[0] - a[0]);
  const value = Math.sin(dLat / 2) ** 2
    + Math.cos(radians(a[1])) * Math.cos(radians(b[1])) * Math.sin(dLng / 2) ** 2;
  return EARTH_RADIUS_MI * 2 * Math.atan2(Math.sqrt(value), Math.sqrt(Math.max(0, 1 - value)));
}

function polygons(geometry: GeoJSON.Geometry | null | undefined): Position[][][] {
  if (!geometry) return [];
  if (geometry.type === 'Polygon') return [geometry.coordinates as Position[][]];
  if (geometry.type === 'MultiPolygon') return geometry.coordinates as Position[][][];
  if (geometry.type === 'GeometryCollection') return geometry.geometries.flatMap(polygons);
  return [];
}

function lineParts(geometry: GeoJSON.Geometry | null | undefined): Position[][] {
  if (!geometry) return [];
  switch (geometry.type) {
    case 'Point': {
      const point = position(geometry.coordinates);
      return point ? [[point]] : [];
    }
    case 'MultiPoint':
      return geometry.coordinates.map(position).filter((point): point is Position => Boolean(point)).map(point => [point]);
    case 'LineString':
      return [geometry.coordinates.map(position).filter((point): point is Position => Boolean(point))];
    case 'MultiLineString':
      return geometry.coordinates.map(line => line.map(position).filter((point): point is Position => Boolean(point)));
    case 'Polygon':
      return geometry.coordinates.map(line => line.map(position).filter((point): point is Position => Boolean(point)));
    case 'MultiPolygon':
      return geometry.coordinates.flatMap(polygon => polygon.map(line => line.map(position).filter((point): point is Position => Boolean(point))));
    case 'GeometryCollection':
      return geometry.geometries.flatMap(lineParts);
  }
}

function geometryPoints(geometry: GeoJSON.Geometry | null | undefined): Position[] {
  return lineParts(geometry).flat();
}

function geometrySegments(geometry: GeoJSON.Geometry | null | undefined): Array<[Position, Position]> {
  return lineParts(geometry).flatMap(line => line.slice(1).map((end, index): [Position, Position] => [line[index], end]));
}

function cross(a: Position, b: Position, c: Position): number {
  return (b[0] - a[0]) * (c[1] - a[1]) - (b[1] - a[1]) * (c[0] - a[0]);
}

function onSegment(a: Position, b: Position, p: Position): boolean {
  return Math.abs(cross(a, b, p)) < 1e-10
    && p[0] >= Math.min(a[0], b[0]) - 1e-10
    && p[0] <= Math.max(a[0], b[0]) + 1e-10
    && p[1] >= Math.min(a[1], b[1]) - 1e-10
    && p[1] <= Math.max(a[1], b[1]) + 1e-10;
}

function segmentIntersection(a: Position, b: Position, c: Position, d: Position): Position | null {
  const denominator = (b[0] - a[0]) * (d[1] - c[1]) - (b[1] - a[1]) * (d[0] - c[0]);
  if (Math.abs(denominator) < 1e-12) {
    for (const candidate of [a, b, c, d]) {
      if (onSegment(a, b, candidate) && onSegment(c, d, candidate)) return [...candidate];
    }
    return null;
  }
  const t = ((c[0] - a[0]) * (d[1] - c[1]) - (c[1] - a[1]) * (d[0] - c[0])) / denominator;
  const u = ((c[0] - a[0]) * (b[1] - a[1]) - (c[1] - a[1]) * (b[0] - a[0])) / denominator;
  if (t < -1e-10 || t > 1 + 1e-10 || u < -1e-10 || u > 1 + 1e-10) return null;
  return [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t];
}

function pointInRing(pointValue: Position, ring: Position[]): boolean {
  let inside = false;
  for (let index = 0, previous = ring.length - 1; index < ring.length; previous = index++) {
    const a = ring[index];
    const b = ring[previous];
    if (onSegment(a, b, pointValue)) return true;
    if ((a[1] > pointValue[1]) !== (b[1] > pointValue[1])
      && pointValue[0] < (b[0] - a[0]) * (pointValue[1] - a[1]) / (b[1] - a[1]) + a[0]) {
      inside = !inside;
    }
  }
  return inside;
}

function pointInPolygon(pointValue: Position, polygon: Position[][]): boolean {
  return Boolean(polygon[0]?.length)
    && pointInRing(pointValue, polygon[0])
    && !polygon.slice(1).some(ring => pointInRing(pointValue, ring));
}

function nearestPointOnSegment(pointValue: Position, start: Position, end: Position, longitudeScale: number): Position {
  const px = pointValue[0] * longitudeScale;
  const ax = start[0] * longitudeScale;
  const bx = end[0] * longitudeScale;
  const dx = bx - ax;
  const dy = end[1] - start[1];
  const denominator = dx * dx + dy * dy;
  const t = denominator === 0
    ? 0
    : Math.max(0, Math.min(1, ((px - ax) * dx + (pointValue[1] - start[1]) * dy) / denominator));
  return [start[0] + (end[0] - start[0]) * t, start[1] + (end[1] - start[1]) * t];
}

function planarDistanceSquared(a: Position, b: Position, longitudeScale: number): number {
  const dx = (a[0] - b[0]) * longitudeScale;
  const dy = a[1] - b[1];
  return dx * dx + dy * dy;
}

function pointToSegmentBoundsSquared(pointValue: Position, start: Position, end: Position, longitudeScale: number): number {
  const x = pointValue[0] * longitudeScale;
  const minX = Math.min(start[0], end[0]) * longitudeScale;
  const maxX = Math.max(start[0], end[0]) * longitudeScale;
  const dx = x < minX ? minX - x : x > maxX ? x - maxX : 0;
  const minY = Math.min(start[1], end[1]);
  const maxY = Math.max(start[1], end[1]);
  const dy = pointValue[1] < minY ? minY - pointValue[1] : pointValue[1] > maxY ? pointValue[1] - maxY : 0;
  return dx * dx + dy * dy;
}

function boundsForPoints(points: Position[]): Bounds | null {
  if (!points.length) return null;
  const result = { minX: Infinity, minY: Infinity, maxX: -Infinity, maxY: -Infinity };
  for (const pointValue of points) {
    result.minX = Math.min(result.minX, pointValue[0]);
    result.minY = Math.min(result.minY, pointValue[1]);
    result.maxX = Math.max(result.maxX, pointValue[0]);
    result.maxY = Math.max(result.maxY, pointValue[1]);
  }
  return result;
}

function boundsOverlap(a: Bounds | null, b: Bounds | null): boolean {
  return Boolean(a && b && a.minX <= b.maxX && a.maxX >= b.minX && a.minY <= b.maxY && a.maxY >= b.minY);
}

function footprintIndex(footprint: GeoJSON.FeatureCollection): FootprintIndex {
  const prior = footprintIndexCache.get(footprint);
  if (prior) return prior;
  const geometries = (footprint?.features || [])
    .map(value => value.geometry)
    .filter((geometry): geometry is GeoJSON.Geometry => Boolean(geometry && polygons(geometry).length));
  const sitePolygons = geometries.flatMap(polygons);
  const points = geometries.flatMap(geometryPoints);
  const segments = geometries.flatMap(geometrySegments);
  const meanLatitude = points.length ? points.reduce((sum, pointValue) => sum + pointValue[1], 0) / points.length : 0;
  const result = {
    geometries,
    polygons: sitePolygons,
    points,
    segments,
    longitudeScale: Math.cos(radians(meanLatitude)),
    bounds: boundsForPoints(points),
  };
  footprintIndexCache.set(footprint, result);
  return result;
}

export function distanceToFootprint(
  featureValue: GeoJSON.Feature | GeoJSON.Geometry,
  footprint: GeoJSON.FeatureCollection,
): DistanceResult {
  const subject = featureValue?.type === 'Feature' ? featureValue.geometry : featureValue;
  const cached = subject && distanceCache.get(subject)?.get(footprint);
  if (cached) return cached;
  const save = (result: DistanceResult): DistanceResult => {
    if (!subject) return result;
    let byFootprint = distanceCache.get(subject);
    if (!byFootprint) {
      byFootprint = new WeakMap<object, DistanceResult>();
      distanceCache.set(subject, byFootprint);
    }
    byFootprint.set(footprint, result);
    return result;
  };
  const site = footprintIndex(footprint);
  if (!subject || site.geometries.length === 0) {
    return save({ miles: null, label: UNKNOWN_DISTANCE, from: null, to: null });
  }

  const subjectPoints = geometryPoints(subject);
  const subjectSegments = geometrySegments(subject);
  const subjectPolygons = polygons(subject);
  const subjectBounds = boundsForPoints(subjectPoints);
  if (subjectPoints.length === 0 || site.segments.length === 0) {
    return save({ miles: null, label: UNKNOWN_DISTANCE, from: null, to: null });
  }

  const overlappingBounds = boundsOverlap(subjectBounds, site.bounds);
  if (subject.type === 'Point' && overlappingBounds && site.polygons.some(polygon => pointInPolygon(subjectPoints[0], polygon))) {
    return save({ miles: 0, label: 'Within site', from: subjectPoints[0], to: subjectPoints[0] });
  }

  const subjectWithinSite = overlappingBounds && subjectPoints.some(pointValue => site.polygons.some(polygon => pointInPolygon(pointValue, polygon)));
  const siteWithinSubject = overlappingBounds && subjectPolygons.length > 0 && site.points.some(pointValue => subjectPolygons.some(polygon => pointInPolygon(pointValue, polygon)));
  if (subjectWithinSite || siteWithinSubject) {
    const shared = subjectWithinSite
      ? subjectPoints.find(pointValue => site.polygons.some(polygon => pointInPolygon(pointValue, polygon))) || null
      : site.points.find(pointValue => subjectPolygons.some(polygon => pointInPolygon(pointValue, polygon))) || null;
    return save({ miles: 0, label: 'Intersects site', from: shared, to: shared });
  }

  if (overlappingBounds) {
    for (const [subjectStart, subjectEnd] of subjectSegments) {
      for (const [siteStart, siteEnd] of site.segments) {
        if (Math.max(subjectStart[0], subjectEnd[0]) < Math.min(siteStart[0], siteEnd[0])
          || Math.min(subjectStart[0], subjectEnd[0]) > Math.max(siteStart[0], siteEnd[0])
          || Math.max(subjectStart[1], subjectEnd[1]) < Math.min(siteStart[1], siteEnd[1])
          || Math.min(subjectStart[1], subjectEnd[1]) > Math.max(siteStart[1], siteEnd[1])) continue;
        const intersection = segmentIntersection(subjectStart, subjectEnd, siteStart, siteEnd);
        if (intersection) {
          return save({ miles: 0, label: 'Intersects site', from: intersection, to: intersection });
        }
      }
    }
  }

  let best: { score: number; from: Position; to: Position } | null = null;
  const consider = (from: Position, to: Position) => {
    const score = planarDistanceSquared(from, to, site.longitudeScale);
    if (!best || score < best.score) best = { score, from, to };
  };
  const firstSiteSegment = site.segments[0];
  consider(subjectPoints[0], nearestPointOnSegment(subjectPoints[0], firstSiteSegment[0], firstSiteSegment[1], site.longitudeScale));
  for (const subjectPoint of subjectPoints) {
    for (const [siteStart, siteEnd] of site.segments) {
      const current = best as { score: number } | null;
      if (current && pointToSegmentBoundsSquared(subjectPoint, siteStart, siteEnd, site.longitudeScale) >= current.score) continue;
      consider(subjectPoint, nearestPointOnSegment(subjectPoint, siteStart, siteEnd, site.longitudeScale));
    }
  }
  for (const sitePoint of site.points) {
    for (const [subjectStart, subjectEnd] of subjectSegments) {
      const current = best as { score: number } | null;
      if (current && pointToSegmentBoundsSquared(sitePoint, subjectStart, subjectEnd, site.longitudeScale) >= current.score) continue;
      consider(nearestPointOnSegment(sitePoint, subjectStart, subjectEnd, site.longitudeScale), sitePoint);
    }
  }
  const nearest = best as { score: number; from: Position; to: Position } | null;
  if (!nearest) return save({ miles: null, label: UNKNOWN_DISTANCE, from: null, to: null });
  const miles = haversineMiles(nearest.from, nearest.to);
  return save({
    miles,
    label: `${formatDistance(miles)} mi from site`,
    from: nearest.to,
    to: nearest.from,
  });
}

export function resolveTarget(target: FeatureTarget, data: BriefingLayerData): GeoJSON.Feature[] {
  const wanted = new Set(target.values.map(String));
  const matched = features(data, target.layer).filter(value => {
    const candidate = featureProperties(value)[target.property];
    return candidate != null && wanted.has(String(candidate));
  });
  if (matched.length > 0) return matched;
  const nearestKeys: Record<string, string[]> = {
    substations: ['substation', 'secondSubstation'],
    airports: ['airport', 'majorAirport', 'nearestCommercial', 'pavedRunway', 'altPavedRunway', 'altPavedRunway2'],
    gasPlants: ['gasPlant'],
    railroads: ['railroad'],
  };
  const nearest = (nearestKeys[target.layer] || [])
    .map(key => data.nearestFeatures?.[key])
    .filter((value): value is NearestFeature => Boolean(value))
    .filter(value => {
      const candidate = (value as unknown as Record<string, unknown>)[target.property];
      return candidate != null && wanted.has(String(candidate));
    })
    .flatMap(value => {
      const coordinates = position([value.lng, value.lat]);
      return coordinates ? [{
        type: 'Feature' as const,
        properties: { ...value },
        geometry: { type: 'Point' as const, coordinates },
      }] : [];
    });
  if (nearest.length > 0) return nearest;
  if (target.point && validPosition(target.point)) {
    const properties: Properties = {};
    if (target.values[0] != null) properties[target.property] = target.values[0];
    if (target.name != null) properties.name = target.name;
    return [{
      type: 'Feature',
      properties,
      geometry: { type: 'Point', coordinates: target.point },
    }];
  }
  return [];
}

function withMappedDistances(facts: LayerFacts, data: BriefingLayerData, footprint: GeoJSON.FeatureCollection, limit = 3): LayerFacts {
  const uvaldeAssembly = isUvaldeBriefing({ parcel: data.parcel, carveOutAggregate: { slug: data.carveOutSlug } });
  const measured = new Set<string>();
  let hasMappedDistance = false;
  const items = facts.items.slice(0, limit).map(item => {
    if (!item.target) return item;
    const key = JSON.stringify([item.target.layer, item.target.property, item.target.values, item.target.point]);
    if (measured.has(key)) return item;
    measured.add(key);
    let nearest: ReturnType<typeof distanceToFootprint> | null = null;
    for (const value of resolveTarget(item.target, data)) {
      const result = distanceToFootprint(value, footprint);
      if (result.miles == null) continue;
      if (!nearest || nearest.miles == null || result.miles < nearest.miles) nearest = result;
    }
    if (!nearest) return item;
    hasMappedDistance = true;
    const label = uvaldeAssembly ? nearest.label.replace('from site', '· assembly edge').replace('site', 'assembly') : nearest.label;
    return { ...item, text: `${item.text} · ${label}` };
  });
  return {
    items,
    source: hasMappedDistance ? `${facts.source} · ${uvaldeAssembly ? 'Approx. straight-line from either tract boundary to shown geometry; offered-tract distances are in Site Access' : 'Approx. straight-line from site boundary'}` : facts.source,
  };
}

function noSummary(source: string): LayerFacts {
  return { items: [{ text: 'No summary recorded' }], source };
}

function factsForFeatures(
  data: BriefingLayerData,
  dataKey: string,
  source: string,
  describe: (properties: Properties) => string | null,
): LayerFacts {
  const rows = features(data, dataKey);
  const items = rows.map(value => ({ text: describe(featureProperties(value)), target: targetForFeature(dataKey, value) }))
    .filter((item): item is { text: string; target: FeatureTarget | null } => Boolean(item.text))
    .slice(0, 3);
  return items.length ? { items, source } : noSummary(source);
}

export function getLayerFacts(
  memberId: string,
  data: BriefingLayerData,
  footprint: GeoJSON.FeatureCollection,
): LayerFacts {
  let result: LayerFacts;
  const floor = largeLineFloor(data);
  // The curated large-line lists are the only facts that outgrow the three-item cap.
  let limit = 3;
  switch (memberId) {
    case 'briefing-pipelines-gas': {
      if (floor != null) {
        const systems = data.pipelines.filter(value => (!value.is_gathering || value.headline_override) && (positiveNumber(value.diameter_inches) ?? 0) >= floor);
        result = largeLineFacts(data, 'pipelinesGas', systems, 'Briefing pipeline data');
        limit = Infinity;
        break;
      }
      const pipeline = data.pipelines.find(value => !value.is_gathering);
      if (!pipeline) result = noSummary('Briefing pipeline data');
      else {
        const target: FeatureTarget = {
          layer: 'pipelinesGas',
          property: 'id',
          values: [String(pipeline.id)],
          name: pipeline.name || undefined,
        };
        result = {
          items: [
            { text: pipeline.name || pipeline.operator || 'Mapped transmission line', target },
            ...(positiveNumber(pipeline.diameter_inches) ? [{ text: `${formatNumber(Number(pipeline.diameter_inches))}″ diameter`, target }] : []),
          ],
          source: 'Briefing pipeline data',
        };
      }
      break;
    }
    case 'briefing-pipelines-desert-sw':
      result = factsForFeatures(data, 'pipelinesDesertSw', 'Pipeline map', properties => text(properties.name) || text(properties.operator) || 'Mapped pipeline');
      break;
    case 'briefing-pipelines-gathering':
      if (floor != null) {
        const systems = data.pipelines
          .filter(value => value.is_gathering && !value.headline_override && (positiveNumber(value.diameter_inches) ?? 0) >= floor)
          .sort((a, b) => Number(a.distance_miles) - Number(b.distance_miles));
        result = largeLineFacts(data, 'pipelinesGathering', systems, 'Pipeline map · oil and gas gathering');
        limit = Infinity;
        break;
      }
      result = factsForFeatures(data, 'pipelinesGathering', 'Pipeline map · oil and gas gathering', properties => text(properties.name) || text(properties.operator) || 'Mapped gathering line');
      break;
    case 'briefing-gas-plants':
      result = factsForFeatures(data, 'gasPlants', 'Gas processing map records', properties => titleCase(properties.name) || 'Mapped gas processing plant');
      break;
    case 'briefing-compressors':
      result = factsForFeatures(data, 'compressors', 'HIFLD · recorded horsepower', properties => {
        const horsepower = positiveNumber(properties.horsepower);
        return `${titleCase(properties.name) || 'Compressor'}${horsepower ? ` · ${formatNumber(horsepower)} hp` : ''}`;
      });
      break;
    case 'briefing-wetlands-fill': {
      const rows = features(data, 'wetlands');
      const classes = unique(rows.map(value => text(featureProperties(value).name) || text(featureProperties(value).wetland_type)));
      result = classes.length ? {
        items: classes.slice(0, 3).map(className => ({
          text: className,
          target: groupedTarget('wetlands', rows.filter(value => (text(featureProperties(value).name) || text(featureProperties(value).wetland_type)) === className)),
        })),
        source: 'USFWS NWI · mapped classes',
      } : noSummary('USFWS NWI · mapped classes');
      break;
    }
    case 'briefing-flood-zones-fill': {
      const rows = features(data, 'floodZones');
      const labelFor = (value: GeoJSON.Feature) => {
        const properties = featureProperties(value);
        return properties.source === 'fema_pending' ? `${pendingFemaLabel(text(properties.effective_date))} · Zone ${text(properties.zone_code)}`
          : properties.source === 'twdb_screening' ? 'TWDB 1% screen' : text(properties.zone_code) ? `FEMA Zone ${text(properties.zone_code)}` : null;
      };
      const zones = unique(rows.map(labelFor));
      const twdb = rows.some(value => featureProperties(value).source === 'twdb_screening');
      const pending = rows.some(value => featureProperties(value).source === 'fema_pending');
      result = zones.length ? {
        items: zones.slice(0, 3).map(zone => ({ text: zone, target: groupedTarget('floodZones', rows.filter(value => labelFor(value) === zone)) })),
        source: pending ? 'FEMA effective and pending mapping labeled separately' : twdb ? 'TWDB 1% screen · non-regulatory' : 'FEMA · mapped area',
      } : noSummary('FEMA 100-year floodplain map');
      break;
    }
    case 'briefing-oil-gas-wells':
      result = factsForFeatures(data, 'oilGasWells', 'RRC · mapped well records', properties => {
        const name = text(properties.name) || text(properties.id) || 'Mapped well';
        return properties.status ? `${name} · ${titleCase(String(properties.status).replace(/_/g, ' '))}` : name;
      });
      break;
    case 'briefing-substations': {
      const nearest = data.nearestFeatures?.substation;
      if (nearest) {
        const target = targetForNearest('substations', nearest);
        result = {
          items: [
            { text: nearest.name || 'Nearest substation', target },
            ...(positiveNumber(nearest.max_voltage_kv) ? [{ text: `${formatNumber(Number(nearest.max_voltage_kv))} kV`, target }] : []),
          ],
          source: 'Briefing nearest-feature record',
        };
      } else {
        result = factsForFeatures(data, 'substations', 'Mapped substation records', properties => titleCase(properties.name) || 'Mapped substation');
      }
      break;
    }
    case 'briefing-osm-switchyards':
      result = factsForFeatures(data, 'osmSwitchyards', 'OpenStreetMap', properties => {
        const voltage = positiveNumber(properties.voltage_kv);
        return `${titleCase(properties.name) || 'Mapped switchyard'}${voltage ? ` · ${formatNumber(voltage)} kV` : ''}`;
      });
      break;
    case 'briefing-carrier-fiber': {
      const rows = features(data, 'carrierRoutes');
      const names = unique(rows.map(value => text(featureProperties(value).name)));
      result = names.length ? {
        items: names.slice(0, 3).map(name => ({ text: name, target: groupedTarget('carrierRoutes', rows.filter(value => text(featureProperties(value).name) === name)) })),
        source: 'Mapped carrier routes',
      } : noSummary('Mapped carrier routes');
      break;
    }
    case 'briefing-fiber-fill': {
      const rows = features(data, 'fiberCoverage');
      const providerNames = unique(rows.flatMap(value => parseList(featureProperties(value).providers).map(provider => text(provider.name) || text(provider.provider))));
      result = providerNames.length ? {
        items: providerNames.slice(0, 3).map(name => ({
          text: name,
          target: groupedTarget('fiberCoverage', rows.filter(value => parseList(featureProperties(value).providers).some(provider => (text(provider.name) || text(provider.provider)) === name))),
        })),
        source: 'FCC · providers in mapped area',
      } : noSummary('Broadband coverage map · in mapped coverage');
      break;
    }
    case 'briefing-data-centers':
      result = factsForFeatures(data, 'dataCenters', 'Mapped data-center records', properties => titleCase(properties.name) || 'Mapped data center');
      break;
    case 'briefing-water-wells': {
      const recorded = (data.parcel.nearby_water_wells || [])
        .filter(value => finiteNumber(value.distance_miles) != null)
        .sort((a, b) => Number(a.distance_miles) - Number(b.distance_miles))[0];
      if (recorded) {
        result = {
          items: [
            { text: `${recorded.proposed_use || 'Water well'} · ${Number(Number(recorded.distance_miles).toFixed(2))} mi recorded · basis unavailable` },
            ...(positiveNumber(recorded.total_depth_ft) ? [{ text: `${formatNumber(Number(recorded.total_depth_ft))} ft deep` }] : []),
            { text: positiveNumber(recorded.yield_gpm) ? `${formatNumber(Number(recorded.yield_gpm))} gpm reported yield` : 'Yield not recorded' },
          ].slice(0, 3),
          source: 'TWDB · existing briefing well record',
        };
      } else {
        const rows = features(data, 'waterWells');
        const depths = rows.map(value => positiveNumber(featureProperties(value).total_depth_ft)).filter((value): value is number => value != null);
        const yields = rows.map(value => positiveNumber(featureProperties(value).yield_gpm)).filter((value): value is number => value != null);
        result = rows.length ? {
          items: [
            { text: `${rows.length} mapped water-well record${rows.length === 1 ? '' : 's'}`, target: groupedTarget('waterWells', rows) },
            ...(depths.length ? [{ text: `${formatNumber(Math.min(...depths))}–${formatNumber(Math.max(...depths))} ft depth recorded` }] : []),
            ...(yields.length ? [{ text: `${formatNumber(Math.min(...yields))}–${formatNumber(Math.max(...yields))} gpm yield recorded` }] : []),
          ].slice(0, 3),
          source: 'TWDB water-well records · mapped records',
        } : noSummary('TWDB water-well records · mapped records');
      }
      break;
    }
    case 'briefing-aquifer-fill':
      result = factsForFeatures(data, 'aquifers', 'Aquifer boundary map', properties => {
        const name = text(properties.name) || 'Mapped aquifer boundary';
        return properties.aquifer_type ? `${name} · ${text(properties.aquifer_type)}` : name;
      });
      break;
    case 'briefing-highways': {
      const rows = features(data, 'highways');
      const names = unique(rows.map(value => roadName(featureProperties(value).name)));
      result = names.length ? {
        items: names.slice(0, 3).map(name => ({ text: name, target: groupedTarget('highways', rows.filter(value => roadName(featureProperties(value).name) === name)) })),
        source: 'Roads in mapped area',
      } : noSummary('Roads in mapped area');
      break;
    }
    case 'briefing-public-roads': {
      // Every numbered route in the box (interstate, state, business, FM) in the server's
      // class order, each hovering all of its segments; county roads and local streets
      // stay on the map and in their click cards but are too many to list here.
      const rows = features(data, 'publicRoads');
      const routes = rows.filter(value => !['CR', 'LS'].includes(text(featureProperties(value).hsys) || ''));
      const names = unique(routes.map(value => text(featureProperties(value).name)));
      result = names.length ? {
        items: names.map(name => ({ text: roadLabel(name) || name, target: groupedTarget('publicRoads', rows.filter(value => text(featureProperties(value).name) === name)) })),
        source: 'TxDOT roadway inventory',
      } : noSummary('TxDOT roadway inventory');
      limit = Infinity;
      break;
    }
    case 'briefing-railroads': {
      const rows = features(data, 'railroads');
      const names = unique(rows.map(value => text(featureProperties(value).name) || text(featureProperties(value).operator)));
      result = names.length ? {
        items: names.slice(0, 3).map(name => ({ text: name, target: groupedTarget('railroads', rows.filter(value => (text(featureProperties(value).name) || text(featureProperties(value).operator)) === name)) })),
        source: 'Railroad map',
      } : noSummary('Railroad map · nearby geometry');
      break;
    }
    case 'briefing-airports': {
      const nearest = data.nearestFeatures || {};
      const candidates = [nearest.airport, nearest.pavedRunway, nearest.nearestCommercial]
        .filter((value): value is NearestFeature => Boolean(value));
      const seen = new Set<string>();
      const items: LayerFact[] = [];
      for (const value of candidates) {
        const identity = value.faa_id || value.name;
        if (seen.has(identity)) continue;
        seen.add(identity);
        const target = targetForNearest('airports', value);
        items.push({ text: titleCase(value.name) || 'Airport', target });
        const runway = positiveNumber(value.runway_max_paved_ft);
        if (runway) items.push({ text: `Longest paved runway: ${formatNumber(runway)} ft`, target });
        if (items.length >= 3) break;
      }
      result = items.length ? { items: items.slice(0, 3), source: 'FAA airport records' } : noSummary('FAA airport records');
      break;
    }
    case 'briefing-access-route':
      result = factsForFeatures(data, 'accessRoute', 'TxDOT road-access screen', properties => properties.kind === 'route' ? text(properties.name) || 'Mapped haul route' : null);
      break;
    case 'briefing-frontage-roads':
      result = factsForFeatures(data, 'frontageRoads', 'TxDOT and OpenStreetMap road-access screen', properties => text(properties.name) || (properties.kind === 'track' ? 'Mapped ranch road' : 'Mapped frontage road'));
      break;
    default:
      result = noSummary('Briefing map data');
  }
  return withMappedDistances({ ...result, items: result.items.slice(0, limit) }, data, footprint, limit);
}

const CARD_SOURCES: Record<string, string> = {
  'briefing-pipelines-desert-sw': 'Pipeline map record',
  'briefing-pipelines-gas': 'RRC / EIA pipeline record',
  'briefing-pipelines-gathering': 'RRC gathering record',
  'briefing-compressors': 'HIFLD infrastructure record',
  'briefing-gas-plants': 'Gas processing record',
  'briefing-substations': 'Mapped substation record',
  'briefing-osm-switchyards': 'OpenStreetMap',
  'briefing-carrier-fiber': 'Carrier route map',
  'briefing-fiber-fill': 'FCC reported coverage',
  'briefing-water-wells': 'TWDB well record',
  'briefing-wetlands-fill': 'USFWS NWI mapped wetland',
  'briefing-flood-zones-fill': 'FEMA flood map',
  'briefing-highways': 'Mapped road alignment',
  'briefing-public-roads': 'TxDOT roadway inventory record',
  'briefing-railroads': 'Mapped railroad record',
  'briefing-airports': 'FAA airport record',
  'briefing-data-centers': 'Mapped facility record',
  'briefing-oil-gas-wells': 'Mapped well record',
};

function exactFeature(clicked: GeoJSON.Feature, definition: LayerDefinition, data: BriefingLayerData): GeoJSON.Feature {
  const clickedId = text(featureProperties(clicked).id);
  if (!clickedId) return clicked;
  return features(data, definition.dataKey).find(value => text(featureProperties(value).id) === clickedId) || clicked;
}

export function getFeatureCard(
  clicked: GeoJSON.Feature & { layer?: { id: string } },
  data: BriefingLayerData,
  footprint: GeoJSON.FeatureCollection,
): FeatureCard | null {
  const clickedLayerId = clicked.layer?.id;
  if (!clickedLayerId) return null;
  const definition = definitionForLayer(clickedLayerId);
  if (!definition || !CARD_SOURCES[definition.id]) return null;
  const full = exactFeature(clicked, definition, data);
  const properties = featureProperties(full);
  const member = definition.id;
  const operator = properties.operator && properties.operator !== properties.name ? titleCase(properties.operator) : null;
  let title = titleCase(properties.name) || definition.label;
  let source = CARD_SOURCES[member];
  let details: string[] = [];

  switch (member) {
    case 'briefing-pipelines-desert-sw':
    case 'briefing-pipelines-gas':
    case 'briefing-pipelines-gathering': {
      const diameter = positiveNumber(properties.diameter_inches);
      const pressure = positiveNumber(properties.maop_psig);
      if (diameter) details.push(`${formatNumber(diameter)}″ diameter`);
      if (operator) details.push(operator);
      details.push(pressure
        ? `${formatNumber(pressure)} psig maximum pressure${properties.maop_source ? ` (${text(properties.maop_source)})` : ''}`
        : 'Pressure not recorded');
      if (member === 'briefing-pipelines-gathering') source += ' · oil and gas lines';
      break;
    }
    case 'briefing-compressors': {
      const horsepower = positiveNumber(properties.horsepower);
      if (horsepower) details.push(`${formatNumber(horsepower)} hp recorded rating`);
      if (operator) details.push(operator);
      break;
    }
    case 'briefing-substations':
    case 'briefing-osm-switchyards': {
      const voltage = positiveNumber(properties.voltage_kv ?? properties.max_voltage_kv);
      if (voltage) details.push(`${formatNumber(voltage)} kV`);
      if (operator) details.push(operator);
      break;
    }
    case 'briefing-carrier-fiber':
      if (operator) details.push(operator);
      break;
    case 'briefing-fiber-fill': {
      title = 'Broadband coverage';
      details = parseList(properties.providers).slice(0, 2)
        .map(provider => text(provider.name) || text(provider.provider))
        .filter((value): value is string => Boolean(value));
      const speed = (value: unknown) => {
        const numeric = positiveNumber(value);
        return numeric ? numeric >= 1000 ? `${formatNumber(numeric / 1000)} Gbps` : `${formatNumber(numeric)} Mbps` : null;
      };
      const down = speed(properties.max_download_mbps);
      const up = speed(properties.max_upload_mbps);
      if (down) details.push(`Reported maximum: ${down} down${up ? ` / ${up} up` : ''}`);
      break;
    }
    case 'briefing-water-wells': {
      title = titleCase(properties.proposed_use || properties.name) || 'Water well';
      const depth = positiveNumber(properties.total_depth_ft);
      const yieldGpm = positiveNumber(properties.yield_gpm);
      if (depth) details.push(`${formatNumber(depth)} ft deep`);
      details.push(yieldGpm ? `${formatNumber(yieldGpm)} gpm reported yield` : 'Yield not recorded');
      break;
    }
    case 'briefing-wetlands-fill':
      title = titleCase(properties.name || properties.wetland_type) || 'Mapped wetland';
      break;
    case 'briefing-flood-zones-fill':
      if (properties.source === 'fema_pending') {
        title = `Pending FEMA Zone ${text(properties.zone_code)}`;
        details = [pendingFemaLabel(text(properties.effective_date)), properties.flood_type === '500yr' ? '0.2% annual-chance flood area' : '1% annual-chance flood area', 'Future mapping; not the current effective determination'];
        source = `FEMA pending county product · ${text(properties.product_id)}`;
      } else if (properties.source === 'twdb_screening') {
        title = 'TWDB flood screen';
        details = ['1% annual-chance flood area', 'Non-regulatory screening'];
        source = 'TWDB State Flood Plan';
      } else {
        title = properties.zone_code ? `FEMA Zone ${text(properties.zone_code)}` : 'Mapped floodplain';
        details = ['1% annual-chance floodplain'];
      }
      break;
    case 'briefing-highways':
      title = roadName(title) || 'Highway';
      break;
    case 'briefing-public-roads': {
      // The three-line road read: what it is, can the load get there, how busy.
      const system = text(properties.system);
      title = roadLabel(properties.name) || system || 'Road';
      const maintainer = text(properties.maintainer);
      const what = [system, maintainer ? `${maintainer}-maintained` : null].filter(Boolean).join(' · ');
      if (what) details.push(what);
      const lanes = positiveNumber(properties.lanes);
      const width = positiveNumber(properties.surface_width_ft);
      const surface = roadSurfaceShort(roadSurfaceFromCode(finiteNumber(properties.surface_code)));
      const spec = [lanes ? `${formatNumber(lanes)} lanes` : null, width ? `${formatNumber(width)} ft wide` : null, surface].filter(Boolean).join(' · ');
      if (spec) details.push(spec);
      const adt = positiveNumber(properties.aadt);
      if (adt) {
        const year = positiveNumber(properties.aadt_year);
        details.push([`Average daily traffic ${formatNumber(adt)}${year ? ` (${year})` : ''}`, truckShareWords(finiteNumber(properties.truck_pct)), trafficWord(adt)].filter(Boolean).join(' · '));
      }
      break;
    }
    case 'briefing-railroads': {
      const networkTypes: Record<string, string> = { M: 'Mainline', O: 'Branch', Y: 'Rail yard', I: 'Industrial track', S: 'Siding', A: 'Abandoned', X: 'Out of service', R: 'Restricted' };
      if (operator) details.push(operator);
      const networkType = networkTypes[String(properties.network_type || '')];
      if (networkType) details.push(networkType);
      break;
    }
    case 'briefing-airports': {
      const airportId = text(properties.arpt_id) || text(properties.faa_id) || text(properties.icao_id);
      if (airportId) title += ` (${airportId})`;
      const runwayData = parseList(properties.runways ?? properties.runway_data);
      const paved = runwayData
        .filter(runway => /^(ASPH|CONC)/i.test(text(runway.surface) || ''))
        .sort((a, b) => Number(b.len_ft || 0) - Number(a.len_ft || 0))[0];
      const length = positiveNumber(paved?.len_ft)
        || positiveNumber(properties.max_paved_runway_ft)
        || positiveNumber(properties.runway_max_paved_ft);
      if (length) {
        const width = positiveNumber(paved?.width_ft);
        details.push(`Longest paved runway: ${formatNumber(length)} ft${width ? ` × ${formatNumber(width)} ft` : ''}`);
      }
      if (properties.airport_class) {
        const className = String(properties.airport_class);
        details.push(className === 'commercial' ? 'Commercial' : className === 'general_aviation' ? 'General aviation' : titleCase(className.replace(/_/g, ' ')) || className);
      }
      break;
    }
    case 'briefing-gas-plants': {
      const capacity = positiveNumber(properties.capacity_mmcf_d);
      if (capacity) details.push(`${formatNumber(capacity)} MMcf/d reported processing capacity`);
      if (operator) details.push(operator);
      if (properties.status) details.push(titleCase(String(properties.status).replace(/_/g, ' ')) || String(properties.status));
      break;
    }
    case 'briefing-data-centers': {
      if (operator) details.push(operator);
      const capacity = positiveNumber(properties.capacity_mw);
      if (capacity) details.push(`${formatNumber(capacity)} MW recorded capacity`);
      if (properties.status) details.push(titleCase(String(properties.status).replace(/_/g, ' ')) || String(properties.status));
      break;
    }
    case 'briefing-oil-gas-wells':
      if (operator) details.push(operator);
      if (properties.status) details.push(titleCase(String(properties.status).replace(/_/g, ' ')) || String(properties.status));
      if (properties.field) details.push(String(properties.field));
      break;
  }

  const target = targetForFeature(definition.dataKey, full);
  if (!target) return null;
  const proximity = distanceToFootprint(full, footprint);
  const uvaldeAssembly = isUvaldeBriefing({ parcel: data.parcel, carveOutAggregate: { slug: data.carveOutSlug } });
  return {
    memberId: definition.id,
    title,
    distance: uvaldeAssembly ? proximity.label.replace('from site', '· assembly edge').replace('site', 'assembly') : proximity.label,
    details: details.filter(Boolean).slice(0, 3),
    source: proximity.miles == null ? source : `${source} · ${uvaldeAssembly ? 'Approx. straight-line from either tract boundary to shown geometry; offered-tract distances are in Site Access' : 'Approx. straight-line from site boundary'}`,
    target,
    feature: full,
    layerId: clickedLayerId,
  };
}
